import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../scripts/AuthContext";
import { useDmSocket } from "../scripts/DmSocketContext";
import { listDmConversations } from "@/lib/dmApi";
import { syncMutedFromConversations } from "@/lib/dmMuteCache";

export function useDmConversations(orgId, opts = {}) {
  const { accessToken } = useAuth();
  const { subscribe } = useDmSocket();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unreadOnly = !!opts.unreadOnly;

  const refresh = useCallback(async () => {
    if (!accessToken || !orgId) {
      setConversations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listDmConversations(accessToken, orgId);
      syncMutedFromConversations(data);
      setConversations(data);
    } catch (err) {
      console.error("Failed to load DM conversations:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribe((event, payload) => {
      if (!orgId) return;
      if (event === "presence.updated") {
        const uid = payload?.user_id;
        if (!uid) return;
        setConversations((prev) =>
          prev.map((c) => {
            if (c.peer?.user_id !== uid) return c;
            return {
              ...c,
              peer: {
                ...c.peer,
                online: !!payload.online,
                presence_status: payload.online
                  ? payload.presence_status || c.peer.presence_status
                  : null,
              },
            };
          })
        );
        return;
      }
      if (String(payload?.org_id) !== String(orgId)) return;
      if (
        event === "dm.message" ||
        event === "dm.read" ||
        event === "dm.message.updated" ||
        event === "dm.message.deleted" ||
        event === "dm.message.reaction" ||
        event === "dm.conversation.deleted" ||
        event === "dm.conversation.updated"
      ) {
        refresh();
      }
    });
  }, [subscribe, orgId, refresh]);

  const visible = unreadOnly
    ? conversations.filter(
        (c) => !c.is_self && !c.muted && (c.unread_count || 0) > 0
      )
    : conversations;

  return {
    conversations: visible,
    allConversations: conversations,
    loading,
    error,
    refresh,
    setConversations,
  };
}
